import { Link } from "react-router-dom";
import { ArrowRight, Linkedin } from "lucide-react";
import { ScrollReveal } from "@/components/ScrollReveal";
import { motion } from "framer-motion";

// Company LinkedIn
const COMPANY_LINKEDIN = "https://www.linkedin.com/company/pkc-property-services";

// Team hero background image - PKC team photo at HQ
const HERO_BG = "https://customer-assets.emergentagent.com/job_industrial-clean-pro/artifacts/j4jl57tj_1703922D-E97E-4FE7-A0B1-D12AD0C8DF22%2B%281%29.png";

const TEAM_MEMBERS = [
  {
    name: "Peter Katsaros",
    role: "Director",
    phone: "0408 851 018",
    linkedin: "https://www.linkedin.com/in/peter-katsaros",
  },
  {
    name: "Andrea Karailis",
    role: "Manager",
    phone: "0408 821 879",
    linkedin: null,
  },
];

export default function TeamPage() {
  return (
    <div data-testid="team-page" className="bg-white">
      {/* Hero - Professional team photo with overlay */}
      <section className="pt-32 pb-20 md:pt-44 md:pb-28 relative overflow-hidden min-h-[70vh] md:min-h-[65vh]">
        {/* Background Image */}
        <div className="absolute inset-0">
          {/* Desktop Image */}
          <img 
            src="https://customer-assets.emergentagent.com/job_industrial-clean-pro/artifacts/k3x3u2no_Untitled%20design%20%2822%29.png" 
            alt="" 
            className="hidden md:block w-full h-full object-cover object-center"
          />
          {/* Mobile Image */}
          <img 
            src="https://customer-assets.emergentagent.com/job_industrial-clean-pro/artifacts/svp7x70c_Safety.%20Quality.%20Accountability.png" 
            alt="" 
            className="block md:hidden w-full h-full object-cover object-center"
          />
          {/* Dark overlay for text legibility */}
          <div className="absolute inset-0 bg-gradient-to-r from-[#1a1a1a]/90 via-[#1a1a1a]/70 to-[#1a1a1a]/50" />
        </div>
        
        <div className="max-w-7xl mx-auto px-6 md:px-12 relative z-10">
          <ScrollReveal>
            <p className="text-[#B8D86B] text-xs font-semibold tracking-[0.3em] uppercase mb-6" style={{ fontFamily: 'Manrope, sans-serif' }}>
              Our Team
            </p>
            <h1 className="text-4xl sm:text-5xl md:text-6xl lg:text-7xl font-semibold text-white leading-[1.1] tracking-tight max-w-4xl" style={{ fontFamily: 'Manrope, sans-serif' }}>
              The People Behind PKC
              <span className="text-[#B8D86B]">.</span>
            </h1>
            <p className="text-white/70 text-base md:text-lg mt-6 max-w-xl leading-relaxed" style={{ fontFamily: 'Manrope, sans-serif' }}>
              A dedicated team of professionals committed to delivering exceptional commercial cleaning outcomes.
            </p>
          </ScrollReveal>
        </div>
      </section>

      {/* Team Grid */}
      <section className="py-20 md:py-28 bg-white">
        <div className="max-w-6xl mx-auto px-6 md:px-12">
          <ScrollReveal>
            <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-6 mb-12 md:mb-16">
              <div>
                <p className="text-[#B8D86B] text-xs font-medium tracking-[0.25em] uppercase mb-4" style={{ fontFamily: 'Manrope, sans-serif' }}>
                  Leadership
                </p>
                <h2 className="text-2xl md:text-3xl font-semibold text-neutral-900 tracking-tight" style={{ fontFamily: 'Manrope, sans-serif' }}>
                  Meet Our Team
                </h2>
              </div>
              <a 
                href={COMPANY_LINKEDIN}
                target="_blank"
                rel="noopener noreferrer"
                className="inline-flex items-center gap-2 text-sm font-medium text-neutral-600 hover:text-[#0077B5] transition-colors"
                style={{ fontFamily: 'Manrope, sans-serif' }}
              >
                <Linkedin className="w-5 h-5" />
                Follow us on LinkedIn
              </a>
            </div>
          </ScrollReveal>
          
          <div className="flex justify-center">
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-6 md:gap-8 max-w-2xl">
            {TEAM_MEMBERS.map((member, idx) => (
              <ScrollReveal key={member.name} delay={idx * 0.1}>
                <motion.div 
                  className="group bg-white border border-neutral-200 p-8 hover:border-[#B8D86B] hover:shadow-lg transition-all duration-300 h-full min-h-[280px] flex flex-col" 
                  data-testid={`team-member-${idx}`}
                  whileHover={{ y: -4 }}
                >
                  {/* Initials Circle */}
                  <div className="w-20 h-20 mx-auto mb-6 rounded-full bg-neutral-900 flex items-center justify-center group-hover:bg-[#B8D86B] transition-colors duration-300">
                    <span className="text-2xl font-semibold text-white" style={{ fontFamily: 'Manrope, sans-serif' }}>
                      {member.name.split(' ').map(n => n[0]).join('')}
                    </span>
                  </div>
                  
                  {/* Info */}
                  <div className="text-center">
                    <h3 className="text-lg font-semibold text-neutral-900 tracking-tight" style={{ fontFamily: 'Manrope, sans-serif' }}>
                      {member.name}
                    </h3>
                    <p className="text-[#B8D86B] text-sm font-medium mt-1" style={{ fontFamily: 'Manrope, sans-serif' }}>
                      {member.role}
                    </p>
                    {member.phone && (
                      <p className="text-neutral-500 text-sm mt-3" style={{ fontFamily: 'Manrope, sans-serif' }}>
                        {member.phone}
                      </p>
                    )}
                    
                    {/* LinkedIn Link */}
                    {member.linkedin && (
                      <a 
                        href={member.linkedin}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="inline-flex items-center gap-1.5 text-xs text-neutral-400 hover:text-[#0077B5] mt-4 transition-colors"
                        style={{ fontFamily: 'Manrope, sans-serif' }}
                      >
                        <Linkedin className="w-4 h-4" />
                        Connect
                      </a>
                    )}
                  </div>
                </motion.div>
              </ScrollReveal>
            ))}
            </div>
          </div>
        </div>
      </section>

      {/* Join Us CTA */}
      <section className="py-24 md:py-32 bg-neutral-50">
        <div className="max-w-4xl mx-auto px-6 md:px-12 text-center">
          <ScrollReveal>
            <p className="text-xs font-semibold tracking-[0.2em] uppercase text-neutral-500 mb-4" style={{ fontFamily: 'Manrope, sans-serif' }}>Careers</p>
            <h2 className="text-4xl md:text-5xl font-semibold text-neutral-900 tracking-tight mb-6" style={{ fontFamily: 'Manrope, sans-serif' }}>
              Join Our Team
            </h2>
            <p className="text-neutral-600 text-lg leading-relaxed mb-8 max-w-2xl mx-auto" style={{ fontFamily: 'Manrope, sans-serif' }}>
              We're always looking for dedicated professionals to join the PKC team. If you're passionate about quality and want to work with Melbourne's leading commercial projects, get in touch.
            </p>
            <Link
              to="/contact"
              data-testid="team-contact-btn"
              className="inline-flex items-center gap-2 bg-[#B8D86B] text-neutral-900 px-10 py-4 text-sm font-semibold hover:bg-[#A3C65A] transition-colors duration-300"
              style={{ fontFamily: 'Manrope, sans-serif' }}
            >
              Get in Touch
              <ArrowRight className="w-4 h-4" />
            </Link>
          </ScrollReveal>
        </div>
      </section>
    </div>
  );
}
